import React, { useState } from 'react';
import { Container, Row, Col, Nav, Alert, Button } from 'react-bootstrap';
import { FaBrain, FaRobot, FaLayerGroup, FaChartBar, FaVial, FaSync, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ModelConfiguration from '../components/ai-models/ModelConfiguration';
import LevelManager from '../components/ai-models/LevelManager';
import ModelDashboard from '../components/ai-models/ModelDashboard';
import ModelTesting from '../components/ai-models/ModelTesting';
import './AiModelsPage.css';

const AiModelsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Clear alerts after timeout
  React.useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Clear any existing alerts when switching tabs
    setError(null);
    setSuccess(null);
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return <ModelDashboard setError={setError} setSuccess={setSuccess} />;
      case 'models':
        return <ModelConfiguration setError={setError} setSuccess={setSuccess} />;
      case 'levels':
        return <LevelManager setError={setError} setSuccess={setSuccess} />;
      case 'testing':
        return <ModelTesting setError={setError} setSuccess={setSuccess} />;
      default:
        return <ModelDashboard setError={setError} setSuccess={setSuccess} />;
    }
  };

  return (
    <div className="ai-models-page">
      <Container fluid className="ai-models-container">
        {/* Streamlined Page Header */}
        <div className="ai-models-header">
          <div className="header-content">
            <div className="header-left">
              <div className="header-title-section">
                <div className="title-container">
                  <FaBrain size={40} className="title-icon" />
                  <div>
                    <h1 className="page-title">AI Models Center</h1>
                    <p className="page-subtitle">
                      Manage LLM configurations, monitor performance, and optimize AI model routing
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="header-actions">
              <Button 
                variant="outline-secondary" 
                onClick={handleGoBack}
                className="back-button"
              >
                <FaArrowLeft className="me-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>

        {/* Global Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="global-alert">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="global-alert">
            {success}
          </Alert>
        )}

        {/* Enhanced Horizontal Navigation Tabs */}
        <div className="ai-tabs-container">
          <div className="ai-horizontal-tabs">
            <div className="ai-tab-nav">
              <button
                className={`ai-tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleTabChange('dashboard')}
              >
                <FaChartBar className="tab-icon" />
                <span className="tab-text">Dashboard</span>
              </button>
              <button
                className={`ai-tab-button ${activeTab === 'models' ? 'active' : ''}`}
                onClick={() => handleTabChange('models')}
              >
                <FaRobot className="tab-icon" />
                <span className="tab-text">Model Config</span>
              </button>
              <button
                className={`ai-tab-button ${activeTab === 'levels' ? 'active' : ''}`}
                onClick={() => handleTabChange('levels')}
              >
                <FaLayerGroup className="tab-icon" />
                <span className="tab-text">Level Manager</span>
              </button>
              <button
                className={`ai-tab-button ${activeTab === 'testing' ? 'active' : ''}`}
                onClick={() => handleTabChange('testing')}
              >
                <FaVial className="tab-icon" />
                <span className="tab-text">Model Testing</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="ai-tab-content">
          {renderActiveTab()}
        </div>
      </Container>
    </div>
  );
};

export default AiModelsPage;